import React, { useState } from "react";
import { Calendar, Plus, ChevronLeft, ChevronRight, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { AppointmentCard } from "@/components/PharmacyDashboard/AppointmentCard";
import { DashboardBookingModal } from "./DashboardBookingModal";
import {
  Appointment,
  useAppointmentsState,
} from "@/lib/dashboard/appointmentsData";
import Skeleton from "@mui/material/Skeleton";
import Box from "@mui/material/Box";
import {
  getPatientAppointmentsByPatientId,
  updateEventStatus,
  sendPatientQuestionnaires,
} from "@/dispatchers/patientDispatchers";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { getVisiblePages } from "@/lib/utils";
import {
  setShowBookingSlots,
  setBookingSlotsMode,
} from "@/dispatchers/scheduleEventsDispatchers";
import {
  setShowPermissionModal,
  setShowPermissionMessage,
} from "@/dispatchers/appDispatchers";
import PermissionRequiredModal from "@/components/PermissionRequiredModal";
import { checkUserPermissions } from "@/utils/checkUserPermissions";
import useTitanApp from "@/hooks/useTitanApp";
import {
  accessTitanverseCalenderUpsert,
  accessTitanverseConsultationUpsert,
} from "@/data/constantStrings";
interface UpcomingAppointmentsProps {
  appointments: Appointment[];
  pharmacists: any[];
  isDarkMode: boolean;
  onSaveBooking: (bookingData: any) => void;
  hasActiveConsultation?: boolean;
  onStartConsultation?: () => void;
  showAppointmentDeleteModal?: (value: boolean) => void;
  onDeleteAppointmentID?: (value: string) => void;
}

export function UpcomingAppointments({
  appointments,
  pharmacists,
  isDarkMode,
  onSaveBooking,
  hasActiveConsultation = false,
  onStartConsultation,
  showAppointmentDeleteModal,
  onDeleteAppointmentID,
}: UpcomingAppointmentsProps) {
  const [openBookingDialog, setOpenBookingDialog] = useState(false);
  const [
    preConsultAppointment,
    setPreConsultAppointment,
  ] = useState<Appointment | null>(null);
  const [
    resendAppointment,
    setResendAppointment,
  ] = useState<Appointment | null>(null);
  const [resendEmail, setResendEmail] = useState("");
  const [resendLoading, setResendLoading] = useState(false);
  const {
    patientAppointmentListLoader,
    selectedPatientDetails,
    handleStartConsultation,
    paginatedAppointments,
    totalPages,
    currentPage,
    itemsPerPage,
    handlePageChange,
  } = useAppointmentsState();
  const { appState } = useTitanApp();
  const { currentPharmacyId } = appState || {};
  const hasConsultationPermission = checkUserPermissions(
    accessTitanverseConsultationUpsert,
  );

  if (patientAppointmentListLoader) {
    return (
      <div
        className={`rounded-xl p-4 transition-colors duration-300 ${
          isDarkMode ? "bg-gray-800" : "bg-white"
        } shadow-sm`}
      >
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Calendar
              className={`w-4 h-4 ${
                isDarkMode ? "text-white" : "text-gray-900"
              }`}
            />
            <h3
              className={`text-sm font-semibold transition-colors duration-300 ${
                isDarkMode ? "text-white" : "text-gray-900"
              }`}
            >
              Upcoming Appointments
            </h3>
          </div>
        </div>

        {/* Skeleton loader cards */}
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <Box
              key={i}
              className={`border rounded-lg p-3 ${
                isDarkMode ? "border-gray-600" : "border-gray-200"
              }`}
            >
              <div className="flex items-start gap-3">
                <Skeleton
                  variant="rectangular"
                  width={48}
                  height={48}
                  className="rounded-lg"
                />
                <div className="flex-1 space-y-2">
                  <Skeleton
                    variant="text"
                    width="60%"
                    height={16}
                    className="rounded"
                  />
                  <Skeleton
                    variant="text"
                    width="40%"
                    height={14}
                    className="rounded"
                  />
                </div>
              </div>
            </Box>
          ))}
        </div>
      </div>
    );
  }

  const handleCloseBookingDialog = () => {
    setOpenBookingDialog(false);
  };

  const getShortDayName = (date: string): string => {
    const [day, month, year] = date?.split("/").map(Number);

    const fullYear = 2000 + year;
    const dateObj = new Date(fullYear, month - 1, day);

    return dateObj.toLocaleDateString("en-US", {
      weekday: "short",
    });
  };

  return (
    <div
      className={`rounded-xl p-4 transition-colors duration-300 ${
        isDarkMode ? "bg-gray-800" : "bg-white"
      } shadow-sm`}
    >
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Calendar
            className={`w-4 h-4 ${isDarkMode ? "text-white" : "text-gray-900"}`}
          />
          <h3
            className={`text-sm font-semibold transition-colors duration-300 ${
              isDarkMode ? "text-white" : "text-gray-900"
            }`}
          >
            Upcoming Appointments
          </h3>
        </div>
        <div className="flex items-center gap-2">
          <Badge
            variant="secondary"
            className={`text-xs ${
              isDarkMode
                ? "bg-gray-700 text-gray-300"
                : "bg-gray-100 text-gray-600"
            }`}
          >
            {appointments.length}
          </Badge>
          <Button
            variant="ghost"
            size="sm"
            className="w-6 h-6 p-0"
            title="Add Appointment"
            onClick={() => {
              if (!checkUserPermissions(accessTitanverseCalenderUpsert)) {
                setShowPermissionModal(true);
                return;
              }
              setBookingSlotsMode("fromAppointment");
              setShowBookingSlots(true);
            }}
          >
            <Plus className="w-3 h-3" />
          </Button>
        </div>
      </div>

      <div
        className={`border rounded-lg overflow-hidden transition-colors duration-300 ${
          isDarkMode
            ? "bg-gray-800 border-gray-600"
            : "bg-white border-gray-200"
        }`}
      >
        {paginatedAppointments?.map((appointment) => {
          console.log(appointment);
          return (
            <div
              key={appointment.id}
              className={`flex border-b last:border-b-0 transition-colors duration-300 ${
                isDarkMode ? "border-gray-700" : "border-gray-200"
              }`}
            >
              {/* Time Column */}

              <div
                className={`w-20 p-3 border-r flex-shrink-0 transition-colors duration-300 flex items-center justify-center ${
                  isDarkMode
                    ? "bg-gray-700 border-gray-600"
                    : "bg-gray-50 border-gray-200"
                }`}
              >
                <div className="text-center">
                  <div
                    className={`text-xs font-medium transition-colors duration-300 ${
                      isDarkMode ? "text-gray-400" : "text-gray-500"
                    }`}
                  >
                    {appointment.date}
                  </div>
                  <div
                    className={`text-sm font-medium transition-colors duration-300 ${
                      isDarkMode ? "text-gray-300" : "text-gray-600"
                    }`}
                  >
                    {getShortDayName(appointment.date)}
                  </div>
                </div>
              </div>

              {/* Content Column */}
              <div className="flex-1 p-3">
                <AppointmentCard
                  appointment={{
                    id: appointment.id,
                    time: appointment.time,
                    service: appointment.type,
                    pharmacist: appointment.practitionerName,
                    status: appointment.status,
                    preConsultationStatus: appointment.preConsultationTemplate
                      ? "completed"
                      : appointment.preConsultationTemplateId
                      ? "outstanding"
                      : undefined,
                  }}
                  isDarkMode={isDarkMode}
                  showPatientInfo={false}
                  onCardClick={() => {
                    if (!hasConsultationPermission) {
                      setShowPermissionMessage(
                        "You don't have permission to start this consultation",
                      );
                      setShowPermissionModal(true);
                      return;
                    }
                    handleStartConsultation(appointment);
                  }}
                  onViewInCalendar={() => {
                    if (!checkUserPermissions(accessTitanverseCalenderUpsert)) {
                      setShowPermissionMessage(
                        "Create or update calendar events",
                      );
                      setShowPermissionModal(true);
                      return;
                    }
                    setBookingSlotsMode("fromAppointment");
                    setShowBookingSlots(true);
                  }}
                  onMarkAsNoShow={async () => {
                    await updateEventStatus({
                      id: appointment.id,
                      EventStatus: 5,
                    });
                    if (
                      selectedPatientDetails?.patientID ||
                      selectedPatientDetails?.patientId
                    ) {
                      getPatientAppointmentsByPatientId(
                        String(
                          selectedPatientDetails.patientID ||
                            selectedPatientDetails.patientId,
                        ),
                      );
                    }
                  }}
                  onPreConsultView={
                    appointment.preConsultationTemplate
                      ? () => setPreConsultAppointment(appointment)
                      : undefined
                  }
                  onPreConsultResend={
                    !appointment.preConsultationTemplate &&
                    appointment.preConsultationTemplateId
                      ? () => {
                          setResendAppointment(appointment);
                          setResendEmail(selectedPatientDetails?.email || "");
                        }
                      : undefined
                  }
                />
              </div>
            </div>
          );
        })}
      </div>

      {/* Pagination Controls */}
      {/* Pagination Controls */}
      {appointments.length > itemsPerPage && totalPages > 1 && (
        <div
          className={`flex items-center justify-between mt-4 pt-3 border-t ${
            isDarkMode ? "border-gray-700" : "border-gray-200"
          }`}
        >
          <div
            className={`text-xs ${
              isDarkMode ? "text-gray-400" : "text-gray-600"
            }`}
          >
            Showing {(currentPage - 1) * itemsPerPage + 1} to{" "}
            {Math.min(currentPage * itemsPerPage, appointments.length)} of{" "}
            {appointments.length}
          </div>

          <div className="flex flex-wrap items-center max-w-full gap-1">
            <button
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className={`p-1 rounded transition-colors duration-300 ${
                currentPage === 1
                  ? isDarkMode
                    ? "text-gray-600 cursor-not-allowed"
                    : "text-gray-400 cursor-not-allowed"
                  : isDarkMode
                  ? "text-gray-300 hover:bg-gray-700"
                  : "text-gray-700 hover:bg-gray-100"
              }`}
            >
              <ChevronLeft className="w-4 h-4" />
            </button>

            {(() => {
              const { start, end } = getVisiblePages(
                currentPage,
                totalPages,
                5,
              ); // max 5 visible

              return (
                <>
                  {start > 1 && (
                    <>
                      <button
                        onClick={() => handlePageChange(1)}
                        className="px-2 py-1 text-xs text-gray-600 rounded hover:bg-gray-100"
                      >
                        1
                      </button>
                      <span className="px-1 text-xs text-gray-400">…</span>
                    </>
                  )}

                  {Array.from(
                    { length: end - start + 1 },
                    (_, i) => start + i,
                  ).map((page) => (
                    <button
                      key={page}
                      onClick={() => handlePageChange(page)}
                      className={`px-2 py-1 text-xs rounded transition-colors ${
                        page === currentPage
                          ? isDarkMode
                            ? "bg-blue-600 text-white"
                            : "bg-blue-500 text-white"
                          : isDarkMode
                          ? "text-gray-300 hover:bg-gray-700"
                          : "text-gray-600 hover:bg-gray-100"
                      }`}
                    >
                      {page}
                    </button>
                  ))}

                  {end < totalPages && (
                    <>
                      <span className="px-1 text-xs text-gray-400">…</span>
                      <button
                        onClick={() => handlePageChange(totalPages)}
                        className="px-2 py-1 text-xs text-gray-600 rounded hover:bg-gray-100"
                      >
                        {totalPages}
                      </button>
                    </>
                  )}
                </>
              );
            })()}

            <button
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
              className={`p-1 rounded transition-colors duration-300 ${
                currentPage === totalPages
                  ? isDarkMode
                    ? "text-gray-600 cursor-not-allowed"
                    : "text-gray-400 cursor-not-allowed"
                  : isDarkMode
                  ? "text-gray-300 hover:bg-gray-700"
                  : "text-gray-700 hover:bg-gray-100"
              }`}
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* Resend Pre-Consultation Dialog */}
      {resendAppointment && (
        <Dialog
          open={!!resendAppointment}
          onOpenChange={(open) => {
            if (!open) {
              setResendAppointment(null);
              setResendEmail("");
              setResendLoading(false);
            }
          }}
        >
          <DialogContent
            className={`max-w-sm ${
              isDarkMode ? "bg-gray-800 text-white border-gray-700" : "bg-white"
            }`}
          >
            <DialogHeader>
              <DialogTitle
                className={isDarkMode ? "text-white" : "text-gray-900"}
              >
                Resend Pre-Consultation
              </DialogTitle>
              <p
                className={`text-sm ${
                  isDarkMode ? "text-gray-400" : "text-gray-600"
                }`}
              >
                The pre-consultation form has not been completed. Resend the
                link to the patient.
              </p>
            </DialogHeader>
            <div className="space-y-3 mt-2">
              <div>
                <label
                  className={`text-xs font-medium block mb-1 ${
                    isDarkMode ? "text-gray-300" : "text-gray-700"
                  }`}
                >
                  Patient Email
                </label>
                <input
                  type="email"
                  value={resendEmail}
                  onChange={(e) => setResendEmail(e.target.value)}
                  className={`w-full px-3 py-2 text-sm border rounded ${
                    isDarkMode
                      ? "bg-gray-700 border-gray-600 text-white"
                      : "bg-white border-gray-300 text-gray-900"
                  }`}
                  placeholder="Enter patient email"
                />
              </div>
              <Button
                onClick={async () => {
                  if (!resendEmail?.trim() || !resendAppointment) return;
                  setResendLoading(true);
                  try {
                    await sendPatientQuestionnaires({
                      preConsultTemlateID:
                        resendAppointment.preConsultationTemplateId,
                      patientID:
                        resendAppointment.patientID ||
                        selectedPatientDetails?.patientID ||
                        selectedPatientDetails?.patientId,
                      pharmacyID: currentPharmacyId || "",
                      email: resendEmail,
                      subject: "Pre-Consultation Questionnaire",
                      message: "",
                    });
                  } catch {
                    // handled by dispatcher
                  } finally {
                    setResendLoading(false);
                    setResendAppointment(null);
                    setResendEmail("");
                  }
                }}
                disabled={!resendEmail?.trim() || resendLoading}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white text-sm h-8"
              >
                {resendLoading ? (
                  "Sending..."
                ) : (
                  <>
                    <Send className="w-3 h-3 mr-1" />
                    Resend Pre-Consult Link
                  </>
                )}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Pre-Consultation Dialog */}
      {preConsultAppointment?.preConsultationTemplate && (
        <Dialog
          open={!!preConsultAppointment}
          onOpenChange={(open) => {
            if (!open) setPreConsultAppointment(null);
          }}
        >
          <DialogContent
            className={`max-w-2xl ${
              isDarkMode ? "bg-gray-800 text-white" : "bg-white"
            }`}
          >
            <DialogHeader>
              <DialogTitle
                className={isDarkMode ? "text-white" : "text-gray-900"}
              >
                Pre-Consultation Questionnaire
              </DialogTitle>
              <p
                className={`text-sm ${
                  isDarkMode ? "text-gray-400" : "text-gray-600"
                }`}
              >
                Completed on{" "}
                {new Date(
                  preConsultAppointment.preConsultationTemplate?.createdOn,
                ).toLocaleString()}
              </p>
            </DialogHeader>
            <div className="space-y-6 mt-4 max-h-[60vh] overflow-y-auto pr-2">
              {preConsultAppointment.preConsultationTemplate?.templateSections?.map(
                (section: any, idx: number) => {
                  const answered = section.userControls?.filter(
                    (q: any) => q.value !== null && q.value !== "",
                  );
                  if (!answered || answered.length === 0) return null;
                  return (
                    <div
                      key={idx}
                      className={`p-4 rounded-lg ${
                        isDarkMode ? "bg-gray-700" : "bg-gray-50"
                      }`}
                    >
                      <h4
                        className={`text-sm font-semibold mb-3 ${
                          isDarkMode ? "text-white" : "text-gray-900"
                        }`}
                      >
                        {section.sectionTitle}
                      </h4>
                      <div className="space-y-2">
                        {answered.map(
                          (q: any, qIdx: number) =>
                            q.type !== "image" && (
                              <div key={qIdx}>
                                <p
                                  className={`text-sm font-medium ${
                                    isDarkMode
                                      ? "text-gray-300"
                                      : "text-gray-800"
                                  }`}
                                >
                                  {q.label}
                                </p>
                                <p
                                  className={`text-sm ${
                                    isDarkMode
                                      ? "text-gray-400"
                                      : "text-gray-600"
                                  }`}
                                >
                                  {q.value}
                                </p>
                              </div>
                            ),
                        )}
                      </div>
                    </div>
                  );
                },
              )}
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
